import React, { useRef, useCallback, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  PencilLine,
  Save,
  Users,
  Bot,
  Info,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChevronsUp,
  ChevronsDown,
  Plus,
  MoreVertical,
  Trash2,
  PanelRightClose,
  PanelRightOpen,
  PanelLeftClose,
  PanelLeftOpen,
  Move,
  Undo2,
  Redo2,
  Copy,
} from "lucide-react";

import * as Popover from "@radix-ui/react-popover";

// Components
import Sidebar from "../components/Form/Sidebar";
// import Toolbar from "../components/Form/Toolbar"; // removed bottom toolbar usage
import RightToolbar from "../components/Form/RightToolbar";
import TutorialSidebar from "../components/Form/TutorialSidebar";
import FormInfo from "../components/Form/FormInfo";
import Canvas from "../components/Form/Canvas";
import ChatBox from "../components/ChatBox";
import Loading from "../pages/loading";
import SuccessOverlay from "../pages/successoverlay";

import { useFormState } from "../hooks/useFormState";
import LineToolBar from "../components/Form/Toolbar/lineToolBar";
import CheckBoxToolBar from "../components/Form/Toolbar/checkBoxToolBar";
import TrueFalseToolbar from "../components/Form/Toolbar/trueFalseToolbar";
import MCSingleSelectToolbar from "../components/Form/Toolbar/MCSingleSelectToolbar";
import MCMultipleSelectToolbar from "../components/Form/Toolbar/MCMultipleSelectToolbar";
import ShortTextboxToolbar from "../components/Form/Toolbar/ShortTextboxToolbar";
import ToggleBoxToolbar from "../components/Form/Toolbar/ToggleBoxToolbar";
import RankingComponentToolbar from "../components/Form/Toolbar/RankingToolbar";
import NumericSliderToolbar from "../components/Form/Toolbar/NumericSliderToolbar";
import DiscreteSliderToolbar from "../components/Form/Toolbar/DiscreteSliderToolbar";
import MatchingPairsToolbar from "../components/Form/Toolbar/MachingPairsToolbar";
import ShapeToolbar from "../components/Form/Toolbar/ShapeToolbar";

const ORIGINAL_WIDTH = 800;

const Form = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const canvasRef = useRef(null);
  const contentRef = useRef(null);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(
    windowWidth >= 768
  );
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(true);
  const [showChatBox, setShowChatBox] = useState(false);
  const [showTutorials, setShowTutorials] = useState(false);
  const [isLoadingQuestions] = useState(false);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      setWindowWidth(newWidth);
      setWindowHeight(newHeight);

      // Keep sidebar state on small screens; user controls visibility via footer toggle
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [windowWidth, windowHeight, isLeftSidebarOpen, isRightSidebarOpen]);

  // console.log(windowHeight, " windowHeight");

  const {
    formTitle,
    setFormTitle,
    formDescription,
    setFormDescription,
    category,
    setCategory,
    formImage,
    setFormImage,
    questions,
    activeQuestionId,
    setActiveQuestionId,
    showFormInfo,
    setShowFormInfo,
    handleDeleteQuestion,
    handleReorderQuestions,
    handleAddQuestion,
    saveToFirebase,
    handleComponentUpdate,
    handleDeleteComponent,
    handleDrop,
    handleQuestionUpdate,
    isSaving,
    // -----toolbar-----
    handleDoubleClick,
    editingComponent,
    showLineEditor,
    showCheckboxEditor,
    showTrueFalseEditor,
    showSingleChoiceEditor,
    showMultiChoiceEditor,
    showShortTextEditor,
    showTextEditor,
    showToggleBoxEditor,
    showRankingEditor,
    showDiscreteSliderEditor,
    showNumericSliderEditor,
    showMatchingPairsEditor,
    showShapeEditor,
    saveFormDetails,
    // arrange helpers
    bringToFront,
    sendToBack,
    bringForward,
    sendBackward,
    undo,
    redo,
    canUndo,
    canRedo,
    duplicateComponent,
  } = useFormState(formId);

  const handleQuestionsAdded = useCallback((count, newQuestionIds) => {
    console.log("🎯 [DEBUG] handleQuestionsAdded called with:", { count, newQuestionIds });
    setShowChatBox(false);
    setSuccessMessage( "question added successfully!");
    setShowSuccessOverlay(true);
    // Reload is handled in ChatBox.js after the overlay duration
  }, []);

  const handleFormImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setFormImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleBack = useCallback(() => {
    navigate("/", { replace: true });
  }, [navigate]);

  useEffect(() => {
    const styleTag = document.createElement("style");
    styleTag.innerHTML = `
      .form-info-overlay {
        z-index: 40 !important;
      }
      .form-info-overlay::before {
        content: "";
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.7);
        backdrop-filter: blur(5px);
        z-index: -1;
      }
      .form-info-content {
        margin-bottom: 2rem !important;
      }
      .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      .hide-scrollbar::-webkit-scrollbar { display: none; }
      /* Modern slim scrollbars for workspace */
      .workspace-scroll { scrollbar-width: thin; }
      .workspace-scroll::-webkit-scrollbar { height: 8px; width: 10px; }
      .workspace-scroll::-webkit-scrollbar-track { background: transparent; }
      .workspace-scroll::-webkit-scrollbar-thumb {
        background: linear-gradient(180deg, #c7d2fe, #93c5fd);
        border-radius: 9999px;
      }
      .workspace-scroll::-webkit-scrollbar-thumb:hover { background: #60a5fa; }
    `;
    document.head.appendChild(styleTag);

    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);

  //

  // Sidebar widths
  const leftSidebarWidth =
    windowWidth < 1024 ? 200 : windowWidth < 1280 ? 240 : 280;
  const rightSidebarWidth = 64;

  // Scales
  const [canvasScale, setCanvasScale] = useState(1);
  const [openQuestionMenuId, setOpenQuestionMenuId] = useState(null);
  const footerScrollRef = useRef(null);
  const [moveMode, setMoveMode] = useState(false);

  const scrollFooterLeft = () => {
    if (footerScrollRef.current) {
      footerScrollRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };
  const scrollFooterRight = () => {
    if (footerScrollRef.current) {
      footerScrollRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  // Popover open/close is managed via Radix onOpenChange; no global click handler needed

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener("resize", handleResize);
    handleResize(); // call once to initialize

    return () => window.removeEventListener("resize", handleResize);
  }, [isLeftSidebarOpen, isRightSidebarOpen]);

  // Auto-fit scale using ResizeObserver on content container
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const updateScale = () => {
      const rect = el.getBoundingClientRect();
      const widthScale = rect.width / ORIGINAL_WIDTH;
      // Scale by width only to keep max width and maintain aspect ratio.
      const newScale = Math.min(widthScale, 1);
      setCanvasScale(Math.max(0.3, newScale));
    };
    updateScale();
    const ro = new ResizeObserver(updateScale);
    ro.observe(el);
    return () => ro.disconnect();
  }, [contentRef]);

  return (
    <div className="min-h-screen bg-gray-100 fixed inset-0 flex flex-col">
      <SuccessOverlay
        show={showSuccessOverlay}
        message={successMessage}
        onClose={() => setShowSuccessOverlay(false)}
      />

      {isSaving && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center">
          <div className="bg-white rounded-lg p-4">
            <Loading
              text="Saving form..."
              type="spinner"
              theme="blue"
              size="large"
            />
          </div>
        </div>
      )}

      {isLoadingQuestions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center">
          <Loading
            text="Updating questions..."
            type="dots"
            theme="light"
            size="large"
          />
        </div>
      )}

      <div className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm px-3 md:px-4 lg:px-6 py-2 md:py-3 lg:py-4 flex items-center overflow-visible h-[56px] lg:h-[72px]">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ChevronLeft size={16} />
          <span className="hidden sm:inline text-xs md:text-sm">
            Back to Forms
          </span>
        </button>

        <div className="ml-auto flex items-center gap-2 md:gap-4 overflow-visible">
          <button
            onClick={() => setShowFormInfo((prev) => !prev)}
            className="flex items-center gap-1 md:gap-2 px-2 lg:px-4 py-1.5 lg:py-2 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow whitespace-nowrap"
          >
            <PencilLine size={14} />
            <span className="hidden sm:inline text-xs md:text-sm">
              Edit Form Info
            </span>
          </button>

          <button
            onClick={saveToFirebase}
            className="flex items-center gap-1 md:gap-2 px-2 lg:px-4 py-1.5 lg:py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors whitespace-nowrap"
            disabled={isSaving}
          >
            <Save size={14} />
            <span className="hidden sm:inline text-xs md:text-sm">
              Save Form
            </span>
          </button>

          <button
            onClick={() => {
              navigate(`/sessions/${formId}`);
            }}
            className="flex items-center gap-1 md:gap-2 px-2 lg:px-4 py-1.5 lg:py-2 rounded-lg bg-gray-500 text-white hover:bg-gray-600 transition-colors whitespace-nowrap"
          >
            <Users size={14} />
            <span className="hidden sm:inline text-xs md:text-sm">
              Go to Sessions
            </span>
          </button>

          <button
            onClick={() => setShowChatBox(true)}
            className="group relative flex items-center gap-2 px-2 lg:px-4 py-1.5 lg:py-2 rounded-xl 
              bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 
              hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600
              text-white shadow-lg transition-all duration-300 hover:shadow-xl
              hover:scale-105 active:scale-95 whitespace-nowrap"
          >
            <div className="relative flex items-center gap-2">
              <Bot
                size={14}
                className="transition-transform group-hover:rotate-12"
              />
              <span className="hidden sm:inline font-medium text-xs md:text-sm">
                AI Assistant
              </span>
              <div
                className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-green-400 
                animate-pulse group-hover:bg-green-300"
              />
            </div>
          </button>

          {/* Tutorials minimal info button */}
          <button
            onClick={() => setShowTutorials(true)}
            className="flex items-center justify-center w-7 h-7 lg:w-9 lg:h-9 rounded-xl border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors"
            aria-label="Open tutorials"
            title="Tutorials"
          >
            <Info size={16} />
          </button>
        </div>
      </div>

      <div className="flex flex-1 mt-[56px] lg:mt-[72px] gap-4 overflow-hidden">
        <div
          className={`relative h-full flex-shrink-0 bg-white shadow-lg transform transition-transform duration-300 ${
            isLeftSidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
          style={{
            width: isLeftSidebarOpen
              ? windowWidth < 768
                ? "200px"
                : leftSidebarWidth + "px"
              : "0",
            maxWidth: windowWidth < 768 ? "100vw" : "85vw",
            overflow: "hidden",
          }}
        >
          <div className="h-full overflow-hidden">
            <Sidebar
              questions={questions}
              activeQuestionId={activeQuestionId}
              setActiveQuestionId={(id) => {
                handleDoubleClick();
                setActiveQuestionId(id);
                if (windowWidth < 768) {
                  setIsLeftSidebarOpen(false);
                }
              }}
              onAddQuestion={handleAddQuestion}
              onDeleteQuestion={handleDeleteQuestion}
              onReorderQuestions={handleReorderQuestions}
              sidebarWidth={isLeftSidebarOpen ? leftSidebarWidth : 0}
              onCloseSidebar={() => setIsLeftSidebarOpen(false)}
            />
          </div>
        </div>

        {/* Main Content Area */}
        <div
          className="flex-1 relative overflow-auto pb-52 px-4 md:px-6 lg:px-8 py-4 md:py-6 overscroll-contain hide-scrollbar"
          ref={contentRef}
          style={{
            backgroundImage:
              "radial-gradient(circle, rgba(0,0,0,0.08) 1px, rgba(0,0,0,0) 1px)",
            backgroundSize: "16px 16px",
            backgroundPosition: "0 0",
          }}
        >
          {/* Content and canvas area should leave space for a right sidebar when open */}
          <FormInfo
            show={showFormInfo}
            onClose={() => setShowFormInfo(false)}
            formTitle={formTitle}
            setFormTitle={setFormTitle}
            formDescription={formDescription}
            setFormDescription={setFormDescription}
            category={category}
            setCategory={setCategory}
            formImage={formImage}
            formId={formId}
            setFormImage={setFormImage}
            saveFormDetails={saveFormDetails}
            handleFormImageUpload={handleFormImageUpload}
          />

          <Canvas
            formRef={formRef}
            question={questions.find((q) => q.id === activeQuestionId)}
            onComponentUpdate={handleComponentUpdate}
            onDeleteComponent={handleDeleteComponent}
            handleDrop={handleDrop}
            showFormInfo={showFormInfo}
            onQuestionUpdate={handleQuestionUpdate}
            windowWidth={windowWidth}
            windowHeight={windowHeight}
            isLeftSidebarOpen={isLeftSidebarOpen}
            leftSidebarWidth={isLeftSidebarOpen ? leftSidebarWidth : 0}
            isRightSidebarOpen={isRightSidebarOpen}
            rightSidebarWidth={isRightSidebarOpen ? rightSidebarWidth : 0}
            handleDoubleClick={handleDoubleClick}
            scale={canvasScale}
            moveMode={moveMode}
            ref={canvasRef}
          />

          {/* Bottom Settings Panels - shown on component double-click (render editors directly) */}
          {showLineEditor && (
            <LineToolBar
              questionId={activeQuestionId}
              isLeftSidebarOpen={isLeftSidebarOpen}
              leftSidebarWidth={leftSidebarWidth}
              isRightSidebarOpen={isRightSidebarOpen}
              windowWidth={windowWidth}
              selectedLine={editingComponent}
              onComponentUpdate={handleComponentUpdate}
              onDeselect={() => handleDoubleClick()}
            />
          )}
          {showCheckboxEditor && (
            <CheckBoxToolBar
              questionId={activeQuestionId}
              isLeftSidebarOpen={isLeftSidebarOpen}
              leftSidebarWidth={leftSidebarWidth}
              isRightSidebarOpen={isRightSidebarOpen}
              windowWidth={windowWidth}
              selectedLine={editingComponent}
              onComponentUpdate={handleComponentUpdate}
              onDeselect={() => handleDoubleClick()}
            />
          )}
          {showTrueFalseEditor && (
            <TrueFalseToolbar
              questionId={activeQuestionId}
              isLeftSidebarOpen={isLeftSidebarOpen}
              leftSidebarWidth={leftSidebarWidth}
              isRightSidebarOpen={isRightSidebarOpen}
              windowWidth={windowWidth}
              selectedComponent={editingComponent}
              onComponentUpdate={handleComponentUpdate}
              onDeselect={() => handleDoubleClick()}
            />
          )}
          {showSingleChoiceEditor && (
            <MCSingleSelectToolbar
              questionId={activeQuestionId}
              isLeftSidebarOpen={isLeftSidebarOpen}
              leftSidebarWidth={leftSidebarWidth}
              isRightSidebarOpen={isRightSidebarOpen}
              windowWidth={windowWidth}
              selectedComponent={editingComponent}
              onComponentUpdate={handleComponentUpdate}
              onDeselect={() => handleDoubleClick()}
            />
          )}
          {showMultiChoiceEditor && (
            <MCMultipleSelectToolbar
              questionId={activeQuestionId}
              isLeftSidebarOpen={isLeftSidebarOpen}
              leftSidebarWidth={leftSidebarWidth}
              isRightSidebarOpen={isRightSidebarOpen}
              windowWidth={windowWidth}
              selectedComponent={editingComponent}
              onComponentUpdate={handleComponentUpdate}
              onDeselect={() => handleDoubleClick()}
            />
          )}
          {showShortTextEditor && (
            <ShortTextboxToolbar
              questionId={activeQuestionId}
              isLeftSidebarOpen={isLeftSidebarOpen}
              leftSidebarWidth={leftSidebarWidth}
              isRightSidebarOpen={isRightSidebarOpen}
              windowWidth={windowWidth}
              selectedComponent={editingComponent}
              onComponentUpdate={handleComponentUpdate}
              compType="short_text"
              onDeselect={() => handleDoubleClick()}
            />
          )}
          {showTextEditor && (
            <ShortTextboxToolbar
              questionId={activeQuestionId}
              isLeftSidebarOpen={isLeftSidebarOpen}
              leftSidebarWidth={leftSidebarWidth}
              isRightSidebarOpen={isRightSidebarOpen}
              windowWidth={windowWidth}
              selectedComponent={editingComponent}
              onComponentUpdate={handleComponentUpdate}
              compType="text"
              onDeselect={() => handleDoubleClick()}
            />
          )}
          {showToggleBoxEditor && (
            <ToggleBoxToolbar
              questionId={activeQuestionId}
              isLeftSidebarOpen={isLeftSidebarOpen}
              leftSidebarWidth={leftSidebarWidth}
              isRightSidebarOpen={isRightSidebarOpen}
              windowWidth={windowWidth}
              selectedComponent={editingComponent}
              onComponentUpdate={handleComponentUpdate}
              onDelete={(componentId) => {
                handleDeleteComponent(activeQuestionId, componentId);
                handleDoubleClick();
              }}
              onDeselect={() => handleDoubleClick()}
            />
          )}
          {showRankingEditor && (
            <RankingComponentToolbar
              questionId={activeQuestionId}
              isLeftSidebarOpen={isLeftSidebarOpen}
              leftSidebarWidth={leftSidebarWidth}
              isRightSidebarOpen={isRightSidebarOpen}
              windowWidth={windowWidth}
              selectedComponent={editingComponent}
              onComponentUpdate={handleComponentUpdate}
              onDeselect={() => handleDoubleClick()}
            />
          )}
          {showDiscreteSliderEditor && (
            <DiscreteSliderToolbar
              questionId={activeQuestionId}
              isLeftSidebarOpen={isLeftSidebarOpen}
              leftSidebarWidth={leftSidebarWidth}
              isRightSidebarOpen={isRightSidebarOpen}
              windowWidth={windowWidth}
              selectedComponent={editingComponent}
              onComponentUpdate={handleComponentUpdate}
              onDeselect={() => handleDoubleClick()}
            />
          )}
          {showMatchingPairsEditor && (
            <MatchingPairsToolbar
              questionId={activeQuestionId}
              isLeftSidebarOpen={isLeftSidebarOpen}
              leftSidebarWidth={leftSidebarWidth}
              isRightSidebarOpen={isRightSidebarOpen}
              windowWidth={windowWidth}
              selectedComponent={editingComponent}
              onComponentUpdate={handleComponentUpdate}
              onDeselect={() => handleDoubleClick()}
            />
          )}
          {showShapeEditor && (
            <ShapeToolbar
              questionId={activeQuestionId}
              isLeftSidebarOpen={isLeftSidebarOpen}
              leftSidebarWidth={leftSidebarWidth}
              isRightSidebarOpen={isRightSidebarOpen}
              windowWidth={windowWidth}
              selectedComponent={editingComponent}
              onComponentUpdate={handleComponentUpdate}
              onDelete={(componentId) => {
                handleDeleteComponent(activeQuestionId, componentId);
                handleDoubleClick();
              }}
              onDeselect={() => handleDoubleClick()}
            />
          )}
          {showNumericSliderEditor && (
            <NumericSliderToolbar
              questionId={activeQuestionId}
              isLeftSidebarOpen={isLeftSidebarOpen}
              leftSidebarWidth={leftSidebarWidth}
              isRightSidebarOpen={isRightSidebarOpen}
              windowWidth={windowWidth}
              selectedComponent={editingComponent}
              onComponentUpdate={handleComponentUpdate}
              onDeselect={() => handleDoubleClick()}
            />
          )}
          {/* Bottom Footer (kept minimal; no add-components toolbar) */}
          <div className="fixed left-0 right-0 bottom-0 z-40 bg-white border-t border-gray-200">
            <div className="flex items-center justify-between px-2 py-1 lg:py-1.5">
              <div className="flex items-center gap-1.5">
                {/* Toggle questions sidebar */}
                <button
                  onClick={() => setIsLeftSidebarOpen((prev) => !prev)}
                  className="px-2 py-1 rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 shadow-sm"
                  aria-label="Toggle left sidebar"
                  title={
                    isLeftSidebarOpen
                      ? "Collapse left sidebar"
                      : "Expand left sidebar"
                  }
                >
                  {isLeftSidebarOpen ? (
                    <PanelLeftClose size={16} />
                  ) : (
                    <PanelLeftOpen size={16} />
                  )}
                </button>

                {/* Scroll controls and question list */}
                <button
                  onClick={scrollFooterLeft}
                  className="hidden md:inline-flex p-1 rounded-md hover:bg-gray-100 text-gray-700"
                  aria-label="Scroll left"
                >
                  <ChevronLeft size={14} />
                </button>
                <div className="hidden md:block relative max-w-[45vw]">
                  <div
                    ref={footerScrollRef}
                    className="hide-scrollbar flex items-center gap-2 overflow-x-auto"
                  >
                    {questions.map((q, index) => (
                      <div key={q.id} className="relative flex items-center">
                        <button
                          onClick={() => setActiveQuestionId(q.id)}
                          className={`px-2 py-0.5 rounded-md border ${
                            q.id === activeQuestionId
                              ? "bg-blue-50 border-blue-300 text-blue-700"
                              : "bg-gray-100 border-gray-200 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          <span className="text-[10px] lg:text-[11px] whitespace-nowrap truncate max-w-[90px] lg:max-w-[100px]">{`Question ${
                            index + 1
                          }`}</span>
                        </button>
                        <Popover.Root
                          open={openQuestionMenuId === q.id}
                          onOpenChange={(open) => {
                            setOpenQuestionMenuId(open ? q.id : null);
                          }}
                        >
                          <Popover.Trigger asChild>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              className="ml-0.5 p-0 text-gray-500 hover:text-gray-700"
                              aria-label="Question menu"
                            >
                              <MoreVertical size={12} />
                            </button>
                          </Popover.Trigger>
                          <Popover.Portal>
                            <Popover.Content
                              side="top"
                              align="start"
                              sideOffset={6}
                              className="z-[60] w-28 bg-white border border-gray-200 rounded-md shadow-md p-1"
                            >
                              <p className="text-xs text-gray-700 text-left px-2 py-1 font-medium">{`Question ${
                                index + 1
                              }`}</p>
                              <button
                                className="w-full flex items-center gap-2 text-xs text-gray-700 text-left px-2 py-1 rounded hover:bg-gray-100"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const targetId = openQuestionMenuId ?? q.id;
                                  setOpenQuestionMenuId(null);
                                  handleDeleteQuestion(targetId);
                                }}
                              >
                                <Trash2 size={12} />
                                Delete
                              </button>
                              <Popover.Arrow className="fill-white" />
                            </Popover.Content>
                          </Popover.Portal>
                        </Popover.Root>
                      </div>
                    ))}
                  </div>
                </div>
                <button
                  onClick={scrollFooterRight}
                  className="hidden md:inline-flex p-1 rounded-md hover:bg-gray-100 text-gray-700"
                  aria-label="Scroll right"
                >
                  <ChevronRight size={14} />
                </button>

                {/* Add question (kept) */}
                <button
                  onClick={handleAddQuestion}
                  className="hidden md:inline-flex p-1 rounded-md hover:bg-gray-100 text-gray-700"
                  aria-label="Add question"
                >
                  <Plus size={14} />
                </button>
              </div>
              <div className="flex items-center gap-2">
                {/* Arrange controls (only when a component is selected) */}
                {editingComponent && (
                  <div className="flex items-center gap-1 mr-1">
                    <button
                      onClick={() => duplicateComponent(editingComponent.id)}
                      className="px-1.5 py-1 rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 shadow-sm"
                      aria-label="Duplicate component"
                      title="Duplicate component"
                    >
                      <Copy size={14} />
                    </button>
                    {(() => {
                      const q = questions.find(
                        (qq) => qq.id === activeQuestionId
                      );
                      const comps = q?.components || [];
                      const idx = comps.findIndex(
                        (c) => c.id === editingComponent.id
                      );
                      const atBack = idx <= 0;
                      const atFront =
                        idx === comps.length - 1 && comps.length > 0;
                      return (
                        <>
                          <button
                            onClick={() => sendToBack(editingComponent.id)}
                            className="px-1.5 py-1 rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 shadow-sm disabled:opacity-40"
                            aria-label="Send to back"
                            title="Send to back"
                            disabled={atBack}
                          >
                            <ChevronsDown size={14} />
                          </button>
                          <button
                            onClick={() => sendBackward(editingComponent.id)}
                            className="px-1.5 py-1 rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 shadow-sm disabled:opacity-40"
                            aria-label="Send backward"
                            title="Send backward"
                            disabled={atBack}
                          >
                            <ChevronDown size={14} />
                          </button>
                          <button
                            onClick={() => bringForward(editingComponent.id)}
                            className="px-1.5 py-1 rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 shadow-sm disabled:opacity-40"
                            aria-label="Bring forward"
                            title="Bring forward"
                            disabled={atFront}
                          >
                            <ChevronUp size={14} />
                          </button>
                          <button
                            onClick={() => bringToFront(editingComponent.id)}
                            className="px-1.5 py-1 rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 shadow-sm disabled:opacity-40"
                            aria-label="Bring to front"
                            title="Bring to front"
                            disabled={atFront}
                          >
                            <ChevronsUp size={14} />
                          </button>
                        </>
                      );
                    })()}
                  </div>
                )}
                {/* Undo/Redo and Move mode toggle */}
                <button
                  onClick={undo}
                  className="px-2 py-1 rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 shadow-sm disabled:opacity-40"
                  aria-label="Undo"
                  title="Undo"
                  disabled={!canUndo}
                >
                  <Undo2 size={16} />
                </button>
                <button
                  onClick={redo}
                  className="px-2 py-1 rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 shadow-sm disabled:opacity-40"
                  aria-label="Redo"
                  title="Redo"
                  disabled={!canRedo}
                >
                  <Redo2 size={16} />
                </button>
                <button
                  onClick={() => setMoveMode((prev) => !prev)}
                  className={`px-2 py-1 rounded-full border shadow-sm flex items-center gap-1 ${
                    moveMode
                      ? "bg-blue-500 text-white border-blue-600 hover:bg-blue-600"
                      : "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
                  }`}
                  aria-label="Toggle move mode"
                  title={moveMode ? "Exit Move mode" : "Enter Move mode"}
                >
                  <Move size={16} />
                  <span className="hidden sm:inline text-xs">
                    {moveMode ? "Move on" : "Move"}
                  </span>
                </button>
                <button
                  onClick={() => setIsRightSidebarOpen((prev) => !prev)}
                  className="px-2 py-1 rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 shadow-sm"
                  aria-label="Toggle right sidebar"
                  title={
                    isRightSidebarOpen
                      ? "Collapse right sidebar"
                      : "Expand right sidebar"
                  }
                >
                  {isRightSidebarOpen ? (
                    <PanelRightClose size={16} />
                  ) : (
                    <PanelRightOpen size={16} />
                  )}
                </button>
              </div>
            </div>
          </div>
          {/* Bottom toolbar removed; editing panels now accessed via right sub-sidebar */}
        </div>

        {/* Right Sidebar (within central container, above footer) */}
        <RightToolbar
          isOpen={isRightSidebarOpen}
          onToggle={() => setIsRightSidebarOpen((prev) => !prev)}
        />
      </div>

      {showChatBox && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="w-11/12 md:w-5/6 lg:w-4/5 h-5/6 max-h-[100vh]">
            <ChatBox
              formId={formId}
              onClose={() => setShowChatBox(false)}
              onQuestionsAdded={handleQuestionsAdded}
            />
          </div>
        </div>
      )}

      {/* Tutorials Drawer */}
      <TutorialSidebar
        isOpen={showTutorials}
        onClose={() => setShowTutorials(false)}
      />
    </div>
  );
};

export default Form;
